import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const AMAZON_LINK = 'https://www.amazon.de';
const KEY_LOGO_URL = '/elemantra-key-logo.png';

const heroImage = '/hero-image.png';

export const HeroSection = () => {
  const { t, countdown } = useLanguage();

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#FDFBF7]"
      data-testid="hero-section"
    >
      {/* Watermark Key Logo */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: 0.03 }}
      >
        <img 
          src={KEY_LOGO_URL} 
          alt="" 
          className="w-[80%] max-w-4xl h-auto object-contain"
          aria-hidden="true"
        />
      </div>

      {/* Grain Texture Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 order-2 lg:order-1 text-center lg:text-left"
          >
            {/* Logo + Brand Name Stack (like packaging) */}
            <div className="mb-8">
              <img 
                src={KEY_LOGO_URL} 
                alt="Elemantra Logo" 
                className="w-48 sm:w-56 lg:w-64 h-auto object-contain mx-auto lg:mx-0 mb-1"
              />
              <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl tracking-[0.08em] text-[#2A2624] uppercase">
                Elemantra
              </h1>
            </div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#B89C6A] mb-6 font-outfit font-medium">
              {t.hero.subtitle}
            </p>
            <p className="font-cormorant text-xl sm:text-2xl text-[#5C5650] leading-relaxed mb-10 italic">
              {t.hero.tagline}
            </p>
            <motion.a
              href={countdown.amazonLink}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block bg-[#2A2624] text-[#FDFBF7] hover:bg-[#B89C6A] hover:text-[#2A2624] transition-colors duration-500 px-10 py-4 text-sm tracking-widest uppercase font-outfit"
              data-testid="hero-buy-amazon"
            >
              {countdown.heroText}
            </motion.a>
            <p className="font-outfit text-xs text-[#B89C6A] mt-3 tracking-wide">
              {countdown.heroSubtext}
            </p>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 order-1 lg:order-2"
          >
            <div className="relative">
              {/* Background Shape */}
              <div className="absolute -inset-4 bg-[#F3EFE6] rounded-sm -z-10" />
              <img
                src={heroImage}
                alt="Elementa Castor Oil"
                className="w-full h-[400px] sm:h-[500px] lg:h-[600px] object-cover"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-px h-16 bg-gradient-to-b from-transparent via-[#B89C6A] to-transparent"
        />
      </motion.div>
    </section>
  );
};
